/**
 * 報告書兼請求書（別記3）・助成金領収書（別記4）API（/api/expense-statements）。F-EXP-03/04
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { expenseKeys } from '../common/api';

export type StatementType = '請求書' | '領収書';

export interface StatementListItem {
  id: number;
  statementType: StatementType;
  fiscalYear: number;
  businessOperatorId: number;
  businessOperatorName: string;
  vesselId: number;
  vesselName: string;
  rosterNo: number | null;
  periodFrom: string;
  periodTo: string;
  claimantName: string | null;
  billingDate: string | null;
  receiptDate: string | null;
  taxableAmount: number;
  taxAmount: number;
  totalAmount: number;
  outputFileName: string | null;
}

export interface StatementLine {
  id: number;
  voyageReportId: number | null;
  sortOrder: number;
  surveyPeriodLabel: string;
  workerName: string;
  wageUnit: number;
  wageDays: number;
  wageAmount: number;
  charterUnit: number;
  charterDays: number;
  charterAmount: number;
  fuelDate: string | null;
  fuelType: string | null;
  fuelQuantity: number;
  fuelUnitPrice: number;
  fuelAmount: number;
  note: string | null;
}

export interface StatementDetail extends StatementListItem {
  periodFrom: string;
  claimantAddress: string | null;
  invoiceRegistrationNo: string | null;
  operatorHasConsumptionTax: boolean;
  taxRatePercent: number | null;
  addresseeName: string;
  lines: StatementLine[];
  subtotals: { wageDays: number; wageAmount: number; charterDays: number; charterAmount: number; fuelQuantity: number; fuelAmount: number };
}

export interface StatementFilter {
  businessOperatorId?: number | null;
  from?: string;
  to?: string;
  excludeVesselIds?: number[];
}

export interface StatementUpdate {
  billingDate?: string | null;
  receiptDate?: string | null;
  claimantName?: string | null;
  claimantAddress?: string | null;
  invoiceRegistrationNo?: string | null;
  taxAmount?: number;
  taxRatePercent?: number | null;
  lines?: { id: number; note: string | null }[];
}

export const statementKeys = {
  all: [...expenseKeys.all, 'statements'] as const,
  list: (type: StatementType, year: number, f: StatementFilter) => [...statementKeys.all, 'list', type, year, f] as const,
  detail: (id: number) => [...statementKeys.all, 'detail', id] as const,
};

export function useStatements(type: StatementType, year: number, f: StatementFilter, enabled = true) {
  return useQuery({
    queryKey: statementKeys.list(type, year, f),
    queryFn: ({ signal }) => api.get<{ items: StatementListItem[]; total: number }>('/expense-statements', { type, fiscalYear: year, ...f }, { signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useStatement(id: number) {
  return useQuery({
    queryKey: statementKeys.detail(id),
    queryFn: ({ signal }) => api.get<StatementDetail>(`/expense-statements/${id}`, undefined, { signal }),
  });
}

export function useGenerateStatements() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      type: StatementType;
      fiscalYear: number;
      businessOperatorId: number | null;
      from: string;
      to: string;
      excludeVesselIds: number[];
      /** 領収日付（領収書のみ。任意） */
      receiptDate?: string | null;
    }) => api.post<{ ids: number[] }>('/expense-statements/generate', { ...input, businessOperatorId: input.businessOperatorId ?? undefined }),
    onSuccess: () => qc.invalidateQueries({ queryKey: statementKeys.all }),
  });
}

export function useUpdateStatement(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StatementUpdate) => api.put<StatementDetail>(`/expense-statements/${id}`, input),
    onSuccess: (d) => {
      qc.setQueryData(statementKeys.detail(id), d);
      return qc.invalidateQueries({ queryKey: statementKeys.all });
    },
  });
}

export function useDeleteStatement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/expense-statements/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: statementKeys.all }),
  });
}
